import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { STATUS } from 'react-joyride'
import router from 'next/router'

import * as S from './styles'
import { routes } from 'core/const/routes'
import MenuFullMobile from '../MenuFullMobile'
import config from '../../../config'
import { useJoyrideContext } from 'contexts/JoyrideTour/JoyrideTourContext'
import * as Analytics from 'utils/analyticsevents'

const stepsTutorialMobile = [
  {
    target: '.passo-mf-alterar-senha',
    title: 'Para alterar a senha cadastrada em seu perfil',
    content:
      'Basta informar a nova senha e a atualização será feita com sucesso.',
    disableBeacon: true
  },
  {
    target: '.passo-mf-presentes',
    title: 'Receba e veja consultorias de presentes enviados no seu perfil',
    content:
      'Caso tenha recebido um presente venha até aqui para aceitar o seu presente e começar a transformar o seu ambiente!',
    disableBeacon: true
  },
  {
    target: '.passo-mf-inspirese',
    title: 'Está precisando de referências para o seu ambiente?',
    content:
      'O Arquiteto de Bolso tem mais de 65 mil projetos realizados para te inspirar.',
    disableBeacon: true
  },
  {
    target: '.passo-mf-assistenteab',
    title:
      'Assistente 24h por inteligência artificial exclusiva para clientes Premium',
    content:
      'Tire dúvidas sobre reforma e decoração com nosso assistente personalizado.',
    disableBeacon: true
  },
  {
    target: '.passo-mf-contato-ajuda',
    title: 'Está com dificuldades e gostaria de ajuda?',
    content:
      'Entre em contato com nosso time de Felicidade do Cliente através do WhatsApp.',
    disableBeacon: true
  },
  {
    target: '.passo-menumob-ambientes',
    title: 'Aqui você encontrará as consultorias contratadas',
    content:
      'Todos os ambientes em um lugar único, além de visualizar etapas de cada consultoria e os passos necessários.',
    disableBeacon: true
  },
  {
    target: '.passo-menumob-dna',
    title:
      'Aqui você encontrará nossa inteligência artificial gratuita para descobrir o DNA do seu ambiente',
    content:
      'Imagens geradas especialmente pra você criadas a partir do seu estilo, tudo de graça.',
    disableBeacon: true
  },
  {
    target: '.passo-menumob-cupons',
    title:
      'Cupons exclusivos de lojas parceiras para você economizar na sua compra',
    content:
      'Transforme seu ambiente com AB economizando no design de ambiente até a compra dos produtos.',
    disableBeacon: true
  },
  {
    target: '.passo-menumob-duvidas',
    title: 'Aqui você encontrará a resposta para as perguntas mais frequentes',
    content:
      'Nosso time preparou todas as respostas com muito carinho para te auxiliar na jornada da sua transformação.',
    disableBeacon: true
  },
  {
    target: '.passo-consultoria',
    title: 'Cada consultoria contratada terá um espaço único para seu ambiente',
    content:
      'É aqui que você vai enviar as informações do ambiente, confirmar a consultoria e visualizar a entrega do seu arquiteto.',
    disableBeacon: true
  },
  {
    target: '.passo-menu-mobile-rodape',
    title: 'Informações e Links Úteis',
    content:
      'Aqui você encontra as informações de contato do nosso time de Felicidade do Cliente e seu horário de atendimento.',
    disableBeacon: true
  }
]

interface IconLinkProps {
  href: string
  src: string
  alt: string
  name: string
  target?: string
  onClick?: () => void
  isActive?: boolean
  className?: string
}

const IconLink = ({
  href,
  src,
  alt,
  name,
  target,
  onClick,
  isActive,
  className
}: IconLinkProps) => {
  const handleClick = () => {
    Analytics.gtmHeaderMenu({ click_text: name })
    if (onClick) onClick()
  }

  return (
    <S.IconContainer
      isActive={isActive}
      onClick={onClick ? handleClick : undefined}
      className={className}
    >
      {!onClick ? (
        <Link href={href} passHref>
          <S.IconContent target={target} onClick={handleClick}>
            <Image src={src} alt={alt} width={32} height={33} />
            <S.IconName>{name}</S.IconName>
          </S.IconContent>
        </Link>
      ) : (
        <S.IconContent target={target}>
          <Image src={src} alt={alt} width={32} height={33} />
          <S.IconName>{name}</S.IconName>
        </S.IconContent>
      )}
    </S.IconContainer>
  )
}

export type MenuMobileAppProps = {
  activeLink?: string | undefined
  handleClickStartTutorialMobile?: () => void
  possuiConsultoriaPremium?: boolean
}

const MenuMobileApp = ({
  activeLink,
  possuiConsultoriaPremium
}: MenuMobileAppProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { setRun, setSteps, dataCallback } = useJoyrideContext()

  const handleClickStartTutorialMobile = () => {
    setRun(true)
    let validSteps = stepsTutorialMobile

    if (!possuiConsultoriaPremium) {
      validSteps = removeStepByTarget(validSteps, '.passo-mf-assistenteab')
    }

    if (router.pathname !== routes.minhasConsultorias) {
      validSteps = removeStepByTarget(validSteps, '.passo-consultoria')
    }

    setSteps(validSteps)
  }

  const removeStepByTarget = (steps, targetToRemove) => {
    return steps.filter((step) => step.target !== targetToRemove)
  }

  const handleJoyrideCallback = () => {
    if (!dataCallback) return

    const { status, action, step } = dataCallback
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

    if (finishedStatuses.includes(status)) {
      setRun(false)
    }

    if (step.target === '.passo-menumob-ambientes' && action === 'update') {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (!dataCallback) return

    handleJoyrideCallback()
  }, [dataCallback])

  return (
    <S.Wrapper className="gtm-app-header-menu">
      <IconLink
        href={routes.minhasConsultorias}
        src="/img/menu/icon-ambientes.svg"
        alt="Logo"
        name="Ambientes"
        isActive={activeLink === routes.minhasConsultorias}
        className="passo-menumob-ambientes"
      />
      <IconLink
        href={routes.match}
        src="/img/menu/icon-dna.svg"
        alt="Logo"
        name="Match"
        isActive={activeLink === routes.match}
        className="passo-menumob-dna"
      />
      <IconLink
        href={routes.cupons}
        src="/img/menu/icon-cupons.svg"
        alt="Logo"
        name="Cupons"
        className="passo-menumob-cupons"
      />
      <IconLink
        href={routes.faq}
        src="/img/menu/icon-duvidas.svg"
        alt="Logo"
        name="Dúvidas"
        isActive={activeLink === routes.faq}
        className="passo-menumob-duvidas"
      />
      <S.IconContainer onClick={() => setIsOpen(true)}>
        <Image
          src="/img/menu/icon-menu.svg"
          alt="Logo"
          width={32}
          height={33}
        />
        <S.IconName>Mais</S.IconName>
      </S.IconContainer>
      {isOpen && (
        <MenuFullMobile
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          handleClickStartTutorialMobile={handleClickStartTutorialMobile}
          possuiConsultoriaPremium={possuiConsultoriaPremium}
        />
      )}
    </S.Wrapper>
  )
}

export default MenuMobileApp
